import { ArrowPathIcon, PencilIcon } from "@heroicons/react/24/outline";
import {
  Avatar,
  Button,
  Chip,
  Input,
  Pagination,
  Select,
  SelectItem,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
} from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { fetchCountries, getPendingProfiles } from "../../services/api";
import { Profile } from "../../types/types";
import { getStatusColor } from "../../utils/fightersAndManagers";
import { getInitials } from "../../utils/utilMethods";
import EditProfileStatus from "../adminActions/EditProfileStatus";

const PendingList: React.FC = () => {
  const { t } = useTranslation("pending");
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [editStatusModalOpen, setEditStatusModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);
  const [selectedGender, setSelectedGender] = useState("");


  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;

  const [role, setRole] = useState("");
  const navigate = useNavigate();

  // Filters
  const [searchName, setSearchName] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");
  
  const genderOptions = ["MALE", "FEMALE", "OTHERS"];
  // Get role
  const getUserRole = (): string => {
    const userJson = localStorage.getItem("loginResponse");
    const user = userJson ? JSON.parse(userJson) : null;
    return user?.profileType || "";
  };

  useEffect(() => {
  
      setRole(getUserRole());
  
      const loadLocations = async () => {
        try {
          setLocations(await fetchCountries());
        } catch (err) {
          console.error('Failed to load countries:', err);
       
        }
      };
      loadLocations();
    }, [t]);
  useEffect(() => {
    setRole(getUserRole());
  }, []);

  // Fetch all pending (both managers + fighters merge)
// Fetch pending profiles (one API)
const fetchProfilesData = async () => {
  setLoading(true);
  try {
    const params = {
      page,
      pageSize,
      // optional filters
      name: searchName || undefined,
      location: selectedLocation || undefined,
        gender: selectedGender || undefined,
    };

    const res = await getPendingProfiles(params);

    setProfiles(res.data || []);
    setTotalPages(res.totalPages || 1);
  } catch (error) {
    setProfiles([]);
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchProfilesData();
  }, [page, searchName, selectedLocation, selectedGender]);

  // Row click
  const handleRowClick = (profile: Profile) => {
    navigate(`/profile?profileId=${profile.id}`);
  };

  // Edit button click
  const handleEditProfile = (event: React.MouseEvent, profile: Profile) => {
    event.stopPropagation();
    setSelectedProfile(profile);
    setEditStatusModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditStatusModalOpen(false);
    setSelectedProfile(null);
    fetchProfilesData();
  };

  // Reset filters
  const resetFilters = () => {
    setSearchName("");
  setSelectedLocation("");
  setSelectedGender("");
  };

  // Filtered data
  const filteredProfiles = profiles.filter((p) => {
    const matchName = searchName
      ? `${p.firstName} ${p.lastName}`
          .toLowerCase()
          .includes(searchName.toLowerCase())
      : true;
    const matchLocation = selectedLocation
      ? p.residenceCountry === selectedLocation
      : true;
    return matchName && matchLocation;
  });

  return (
    <div className="w-full h-screen flex flex-col">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap bg-white p-3 border-b">
        <Input
          placeholder={t("search_name_placeholder", { defaultValue: "Search by Name" })}
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className="w-64 min-w-64"
          size="sm"
        />
        <Select
          placeholder={t("location_placeholder", { defaultValue: "Location" })}
          selectedKeys={selectedLocation ? [selectedLocation] : []}
          onSelectionChange={(keys) =>
            setSelectedLocation(Array.from(keys)[0] as string || "")
          }
          className="w-48 min-w-48 bg-white"
          size="sm"
        >
          {locations.map((location) => (
            <SelectItem key={location}>{location}</SelectItem>
          ))}
        </Select>
        <Select
  placeholder={t("gender_placeholder", { defaultValue: "Gender" })}
  selectedKeys={selectedGender ? [selectedGender] : []}
  onSelectionChange={(keys) =>
    setSelectedGender((Array.from(keys)[0] as string) || "")
  }
  className="w-48 min-w-48 bg-white"
  size="sm"
>
  {genderOptions.map((gender) => (
    <SelectItem key={gender}>
      {t(
        gender === "MALE"
          ? "Male"
          : gender === "FEMALE"
          ? "Female"
          : "Others"
      )}
    </SelectItem>
  ))}
</Select>

        <Tooltip content={t("reset_filters_tooltip", { defaultValue: "Reset Filters" })}>
          <Button
            isIconOnly
            variant="light"
            onClick={resetFilters}
            className="h-10 w-10 text-gray-600 hover:text-gray-800"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </Button>
        </Tooltip>
        {loading && (
          <div className="flex items-center ml-2">
            <Spinner size="sm" color="primary" />
            <span className="ml-2 text-sm text-gray-600">
              {t("loading_text", { defaultValue: "Loading..." })}
            </span>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 p-3">
        <Table
          aria-label="Pending Profiles"
          removeWrapper
          classNames={{
            wrapper: "overflow-auto custom-scrollbar",
            table: "min-w-full",
          }}
        >
          <TableHeader>
            <TableColumn>{t("table.name", { defaultValue: "Name" })}</TableColumn>
            <TableColumn>{t("table.phone", { defaultValue: "Phone" })}</TableColumn>
            <TableColumn>{t("table.location", { defaultValue: "Location" })}</TableColumn>
            <TableColumn>{t("table.status", { defaultValue: "Status" })}</TableColumn>
            <TableColumn>{t("table.actions", { defaultValue: "Actions" })}</TableColumn>
          </TableHeader>
          <TableBody
            items={filteredProfiles.map((profile) => ({
              key: profile.id,
              profile,
            }))}
            emptyContent={t("table.empty", { defaultValue: "No data available" })}
          >
            {(item) => (
              <TableRow
                key={item.key}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("button")) return;
                  handleRowClick(item.profile);
                }}
                className="cursor-pointer bg-white hover:bg-gray-50 border-b border-gray-100 fade-in"
              >
               <TableCell>
  <div className="flex items-center gap-2">
    <Avatar
      src={item.profile.profileImage}
      name={getInitials(
        item.profile.firstName,
        item.profile.lastName
      )}
      size="sm"
    />
    <div className="flex flex-col leading-tight">
      <span className="font-medium">
        {item.profile.firstName} {item.profile.lastName}
      </span>
      <span className="text-xs text-gray-500">
        {item.profile.profileType === "FIGHTER" ? "ATHLETE" : item.profile.profileType}
      </span>
    </div>
  </div>
</TableCell>


                <TableCell>{item.profile.phone || "N/A"}</TableCell>
                <TableCell>{item.profile.residenceCountry || "N/A"}</TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    variant="flat"
                    color={getStatusColor(item.profile.profileStatus)}
                  >
                    {item.profile.profileStatus}
                  </Chip>
                </TableCell>
                <TableCell>
                  {role === "ADMIN" ? (
                    <Tooltip
                      content={t("tooltip.edit", { defaultValue: "Edit" })}
                    >
                      <Button
                        isIconOnly
                        className="h-8 w-8 text-blue-600 hover:bg-blue-50"
                        variant="light"
                        onClick={(e) => handleEditProfile(e, item.profile)}
                      >
                        <PencilIcon className="w-4 h-4" />
                      </Button>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="p-4 flex justify-center">
        <Pagination
          total={totalPages}
          page={page + 1}
          onChange={(p: number) => setPage(p - 1)}
          showControls
        />
      </div>

      {/* Edit Profile Status Modal */}
      {editStatusModalOpen && selectedProfile && (
        <EditProfileStatus
          isOpen={editStatusModalOpen}
          onClose={handleCloseEditModal}
          profile={selectedProfile}
        />
      )}
    </div>
  );
};

export default PendingList;
